import { BASE_URL } from "./backendData";
import { supabase } from "../supabaseClient";

// Account endpoints only serve the signed-in user's own data.
const loadUserData = async (userId) => authedJson(`/loadUser/${userId}`);

const modifyUserData = async (userId, user_data) =>
  authedJson(`/modifyUser/${userId}`, { method: 'POST', body: { user_data } });


const listRestaurants = async () => {
  // Request all rows in one round-trip. Backend default is 20/page so
  // without this we'd only ever show page 1 on the Discover screen.
  // 500 is the current backend cap — safe ceiling for the ~100 live
  // rows and leaves headroom as the directory grows.
  const response = await fetch(`${BASE_URL}/listRestaurants?per_page=500`);
  const restaurantData = await response.json();
  return restaurantData;
};

// Chat endpoints require a signed-in user. Attach the Supabase token when
// there is one; the backend answers 401 when it's missing.
const authHeader = async () => {
  const session = await supabase.auth.getSession();
  const token = session?.data?.session?.access_token;
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Chat endpoints reply { error } with a non-2xx status when the AI is
// unavailable — surface that message instead of an empty reply.
const chatJson = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || 'Chat is temporarily unavailable.');
  return data;
};

// Authenticated JSON call for the social/family endpoints. The backend
// identifies the caller from the token, so no user id is passed. Errors come
// back as { message } (flask-restx aborts) or { error } (auth middleware).
const authedJson = async (path, { method = 'GET', body } = {}) => {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.message || data.error || `Request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return data;
};

// ---- My Table ----
const getMyTableConnections = async () =>
  (await authedJson('/table/connections')).connections || [];

const getMyTableInvites = async () => {
  const { incoming = [], outgoing = [] } = await authedJson('/table/invites');
  return [...incoming, ...outgoing];
};

const getMyTableSuggestions = async () =>
  (await authedJson('/table/discovery')).helpers || [];

const sendMyTableInvite = async ({ email, userId, reason }) =>
  authedJson('/table/invite', {
    method: 'POST',
    body: { invitee_email: email || undefined, invitee_user_id: userId || undefined, invited_reason: reason },
  });

const respondMyTableInvite = async (inviteId, action) =>
  authedJson(`/table/invite/${inviteId}/respond`, { method: 'POST', body: { action } });

const removeMyTableConnection = async (connectionId) =>
  authedJson(`/table/connections/${connectionId}`, { method: 'DELETE' });

// ---- Table questions ----
// The list has no answers; fetch details for questions that have some.
const listTableQuestions = async () => {
  const { questions = [] } = await authedJson('/table/questions');
  return Promise.all(questions.map(async (q) => {
    if (!q.answer_count) return { ...q, answers: [] };
    try {
      const detail = await authedJson(`/table/questions/${q.question_id}`);
      return { ...q, answers: detail.answers || [] };
    } catch {
      return { ...q, answers: [] };
    }
  }));
};

const createTableQuestion = async ({ restaurant_id, template_id, dietary_restriction }) =>
  authedJson('/table/questions', {
    method: 'POST',
    body: { restaurant_id: Number(restaurant_id), template_id, dietary_restriction, visibility: 'table_only' },
  });

const answerTableQuestion = async (questionId, { answer_text, what_ordered }) =>
  authedJson(`/table/questions/${questionId}/answers`, {
    method: 'POST',
    body: { answer_text, what_ordered: what_ordered || undefined },
  });

// ---- Safety signals ----
const listSafetySignals = async (restaurantId) => {
  const qs = restaurantId ? `?restaurant_id=${encodeURIComponent(restaurantId)}` : '';
  return (await authedJson(`/table/signals${qs}`)).signals || [];
};

const createSafetySignal = async (signal) =>
  authedJson('/table/signals', { method: 'POST', body: signal });

// ---- Notifications ----
// There is no notifications table on the server. The inbox is built from
// real events — invites waiting on you and answers to your questions — and
// read/dismissed state is remembered on this device only.
const NOTIF_STATE_KEY = 'cwm.notifications.v1';
const readNotifState = () => {
  try { return JSON.parse(localStorage.getItem(NOTIF_STATE_KEY)) || { read: [], dismissed: [] }; }
  catch { return { read: [], dismissed: [] }; }
};
const writeNotifState = (state) => {
  try { localStorage.setItem(NOTIF_STATE_KEY, JSON.stringify(state)); } catch { /* private mode */ }
};

const listNotifications = async () => {
  const [invites, questions] = await Promise.all([
    authedJson('/table/invites').catch(() => ({ incoming: [] })),
    authedJson('/table/questions').catch(() => ({ questions: [] })),
  ]);
  const items = [
    ...(invites.incoming || []).map((inv) => ({
      id: `invite-${inv.invite_id}`,
      type: 'invite_received',
      at: inv.created_at,
      title: `${inv.other_user?.name || 'A diner'} wants to join your table`,
      body: inv.invited_reason || '',
      deepLink: '/unified/my-table',
      icon: 'mail', tone: 'purple',
    })),
    ...(questions.questions || [])
      .filter((q) => q.is_own && q.answer_count > 0)
      .map((q) => ({
        // Keyed by count so a new answer shows up as unread again.
        id: `answers-${q.question_id}-${q.answer_count}`,
        type: 'table_answer',
        at: q.created_at,
        title: `${q.answer_count} answer${q.answer_count === 1 ? '' : 's'} to your question`,
        body: q.question_text || '',
        deepLink: '/unified/table-questions',
        icon: 'chat', tone: 'blue',
      })),
  ];
  const { read, dismissed } = readNotifState();
  return items
    .filter((n) => !dismissed.includes(n.id))
    .map((n) => ({ ...n, db_id: n.id, unread: !read.includes(n.id) }))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
};

const markNotificationRead = async (id) => {
  const state = readNotifState();
  if (!state.read.includes(id)) state.read.push(id);
  writeNotifState(state);
};
const markAllNotificationsRead = async (ids = []) => {
  const state = readNotifState();
  state.read = Array.from(new Set([...state.read, ...ids]));
  writeNotifState(state);
};
const dismissNotification = async (id) => {
  const state = readNotifState();
  if (!state.dismissed.includes(id)) state.dismissed.push(id);
  writeNotifState(state);
};

// Family Mode is a per-device toggle on the Family page; when on, the chat
// also checks every family member's allergies.
const familyModeOn = () => {
  try { return localStorage.getItem('cwm_family_mode') === '1'; } catch { return false; }
};

// ---- Family ----
// The backend has 3 severities; the Family page shows 4. "Life-threatening"
// is stored as High with a note so it round-trips.
const FAMILY_SEVERITY_TO_API = {
  mild: { severity: 'Low' },
  moderate: { severity: 'Medium' },
  severe: { severity: 'High' },
  life_threatening: { severity: 'High', notes: 'life_threatening' },
};
const familySeverityFromApi = (a) => {
  if (a.notes === 'life_threatening') return 'life_threatening';
  return { Low: 'mild', Medium: 'moderate', High: 'severe' }[a.severity] || 'severe';
};

const getFamilyMembers = async () => {
  const members = await authedJson('/family/members');
  return (Array.isArray(members) ? members : []).map((m) => ({
    ...m,
    relationship: (m.relationship || 'other').toLowerCase(),
    allergies: (m.allergies || []).map((a) => ({
      id: a.id,
      ingredient: a.ingredient,
      severity: familySeverityFromApi(a),
    })),
  }));
};

const toFamilyBody = ({ name, age, relationship }) => ({
  name: (name || '').trim(),
  age: age === '' || age == null || Number.isNaN(Number(age)) ? null : Number(age),
  relationship,
});

// Saves the member, then syncs allergies against what the server has.
const saveFamilyMember = async (member, previousAllergies = []) => {
  const saved = member.id
    ? await authedJson(`/family/members/${member.id}`, { method: 'PUT', body: toFamilyBody(member) })
    : await authedJson('/family/members', { method: 'POST', body: toFamilyBody(member) });
  const memberId = saved.id || member.id;
  const key = (a) => `${a.ingredient.toLowerCase()}|${a.severity}`;
  const wanted = new Map((member.allergies || []).map((a) => [key(a), a]));
  const existing = new Map(previousAllergies.map((a) => [key(a), a]));
  for (const [k, a] of existing) {
    if (!wanted.has(k) && a.id) {
      await authedJson(`/family/members/${memberId}/allergies/${a.id}`, { method: 'DELETE' });
    }
  }
  for (const [k, a] of wanted) {
    if (!existing.has(k)) {
      await authedJson(`/family/members/${memberId}/allergies`, {
        method: 'POST',
        body: { ingredient: a.ingredient, restriction_type: 'Allergy', ...FAMILY_SEVERITY_TO_API[a.severity] },
      });
    }
  }
  return memberId;
};

const deleteFamilyMember = async (memberId) =>
  authedJson(`/family/members/${memberId}`, { method: 'DELETE' });

const startChat = async (user_id, restaurant_id, force_new) => {

  user_id = parseInt(user_id);
  restaurant_id = parseInt(restaurant_id);
  const family_mode = familyModeOn();

  const response = await fetch(`${BASE_URL}/startChat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    body: JSON.stringify({ user_id, restaurant_id, force_new, family_mode }),
  });
  return chatJson(response);
}

const sendMessage = async (chat_id, message) => {
  const response = await fetch(`${BASE_URL}/sendMessage`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    body: JSON.stringify({ chat_id, message })
  });
  return chatJson(response);
}

// The reviewer is taken from the login token; user_id is still sent for older servers.
const submitReview = async (user_id, restaurant_id, chat_id, item, rating, review_text) =>
  authedJson('/submitReview', {
    method: 'POST',
    body: { user_id, restaurant_id, chat_id, item, rating, review_text },
  });

const listRestaurantReviews = async (restaurantId) => {
  const response = await fetch(`${BASE_URL}/reviews/restaurant/${restaurantId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.reviews || [];
};

const loadUserPreferences = async (user_id, restrictionType) =>
  authedJson(`/loadUserPreferences/${user_id}/${restrictionType}`);

const saveUserPreferences = async (user_id, dietary_restrictions, restrictionType) =>
  authedJson(`/saveUserPreferences/${user_id}/${restrictionType}`, {
    method: 'POST',
    body: { dietary_restrictions },
  });

const saveUserPrefrencesAudio = async (user_id, audioBlob) => {
  const formData = new FormData();
  console.log("Received audio blob", audioBlob);
  formData.append('audio_file', audioBlob)
  console.log("formData", formData)
  const response = await fetch(`${BASE_URL}/saveUserPreferencesAudio/${user_id}`, {
    method: 'POST',
    headers: await authHeader(),
    body: formData,
  });
  const data = await response.json();
  return data;
}

const listUserRestaurantReviews = async (user_id, restaurant_id) => {
  const response = await fetch(`${BASE_URL}/reviews/${user_id}/${restaurant_id}`)
  const data = await response.json();
  return data; 
}

const editUserRestaurantReviews = async (user_id, restaurant_id, reviews) => {
  const response = await fetch(`${BASE_URL}/reviews/${user_id}/${restaurant_id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reviews })
  });
  const data = await response.json(); 
  return data;
}

const genImageDish = async(restaurantId, dishName) => {
  const response = await fetch(`${BASE_URL}/imagegen/dish/${restaurantId}/${dishName}`)
  const data = await response.json();
  return data;
}

const menuItemsFromChat = async(chatId) => {
  const response = await fetch(`${BASE_URL}/chat/${chatId}/menu_items`, {
    headers: await authHeader(),
  });
  return chatJson(response);
}

const createRestaurant = async(restaurantData) => {
  const response = await fetch(`${BASE_URL}/restaurant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(restaurantData)
  });
  const data = await response.json(); 
  return data;
}

const deleteRestaurant = async (userId, restaurantId) => {
  const response = await fetch(`${BASE_URL}/restaurant/${restaurantId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({user_id: userId})
  });
  const data = await response.json();
  return data;
};

// Get full restaurant data including menus and documents
const getRestaurant = async (restaurantId) => {
  const response = await fetch(`${BASE_URL}/restaurant/${restaurantId}`);
  const data = await response.json();
  return data;
};

// Update restaurant profile (name, address, description)
const updateRestaurant = async (restaurantId, updates, userId) => {
  const response = await fetch(`${BASE_URL}/restaurant/${restaurantId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...updates, user_id: userId })
  });
  const data = await response.json();
  return data;
};

// Update a specific menu's data
const updateRestaurantMenu = async (restaurantId, menuId, menuData) => {
  const response = await fetch(`${BASE_URL}/restaurant/${restaurantId}/menu/${menuId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ document_data: { menu_data: menuData } })
  });
  const data = await response.json();
  return data;
};

// Add a new menu to restaurant
const addRestaurantMenu = async (restaurantId, menuData) => {
  const response = await fetch(`${BASE_URL}/restaurant/${restaurantId}/menu`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ restaurant_id: restaurantId, menu_data: menuData })
  });
  const data = await response.json();
  return data;
};

// Get audit history for a restaurant
const getAuditHistory = async (restaurantId, queryParams = '') => {
  // Get Supabase session token for authentication
  const session = await supabase.auth.getSession();
  const token = session?.data?.session?.access_token;

  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await fetch(
    `${BASE_URL}/restaurant/${restaurantId}/audit?${queryParams}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch audit history: ${response.statusText}`);
  }

  return response.json();
};

// Helper function to get auth token
const getAuthToken = async () => {
  const session = await supabase.auth.getSession();
  const token = session?.data?.session?.access_token;
  if (!token) {
    throw new Error('No authentication token available');
  }
  return token;
};

// Create a new order
const createOrder = async (orderData) => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/orders`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(orderData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create order');
  }

  return response.json();
};

// Add item to order
const addOrderItem = async (orderId, itemData) => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/orders/${orderId}/items`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(itemData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to add item to order');
  }

  return response.json();
};

// Get order details
const getOrder = async (orderId) => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/orders/${orderId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get order');
  }

  return response.json();
};

// Submit order (change status from pending to submitted)
const submitOrder = async (orderId) => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/orders/${orderId}/submit`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to submit order');
  }

  return response.json();
};

// Get user's orders
const getUserOrders = async () => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/orders`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get orders');
  }

  return response.json();
};

// Create payment intent with Stripe
const createPaymentIntent = async (paymentData) => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/payments/create-intent`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(paymentData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create payment intent');
  }

  return response.json();
};

// Confirm payment after Stripe confirmation
const confirmPayment = async (paymentId, confirmData) => {
  const token = await getAuthToken();
  const response = await fetch(`${BASE_URL}/payments/${paymentId}/confirm`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(confirmData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to confirm payment');
  }

  return response.json();
};

const dataService = {
  loadUserData,
  modifyUserData,
  listRestaurants,
  startChat,
  sendMessage,
  loadUserPreferences,
  saveUserPreferences,
  submitReview,
  saveUserPrefrencesAudio,
  listUserRestaurantReviews,
  editUserRestaurantReviews,
  genImageDish,
  menuItemsFromChat,
  createRestaurant,
  deleteRestaurant,
  getRestaurant,
  updateRestaurant,
  updateRestaurantMenu,
  addRestaurantMenu,
  getAuditHistory,
  createOrder,
  addOrderItem,
  getOrder,
  submitOrder,
  getUserOrders,
  createPaymentIntent,
  confirmPayment,

  // ===========================================================================
  // Stubs for unified C-tier screens that have their own graceful demo
  // fallbacks. Each returns an empty list / null / rejects, so the screen's
  // try/catch path hits the demo state instead of crashing on undefined().
  //
  // When the real backend endpoints land (SafetySignal, Notifications,
  // MyTable social graph, Rewards ledger, Owner analytics, etc.), swap
  // these stubs for real fetch() implementations — the screens are
  // already wired to handle success.
  // ===========================================================================
  listSafetySignals,
  createSafetySignal,
  listRestaurantReviews,
  getFamilyMembers,
  saveFamilyMember,
  deleteFamilyMember,
  listNotifications,
  markNotificationRead,
  dismissNotification,
  markAllNotificationsRead,
  getMyTableConnections,
  getMyTableInvites,
  getMyTableSuggestions,
  sendMyTableInvite,
  acceptMyTableInvite:     (inviteId) => respondMyTableInvite(inviteId, 'accept'),
  declineMyTableInvite:    (inviteId) => respondMyTableInvite(inviteId, 'decline'),
  removeMyTableConnection,
  listTableQuestions,
  createTableQuestion,
  answerTableQuestion,
  getRewardsOverview:      async () => { return null; },
  redeemReward:            async () => { throw new Error('redeemReward: backend not wired yet'); },
  getOwnerAnalytics:       async () => { return null; },
  advanceOrder:            async () => { throw new Error('advanceOrder: backend not wired yet'); },
  cancelOrder:             async () => { throw new Error('cancelOrder: backend not wired yet'); },
  payOrder:                async () => { throw new Error('payOrder: backend not wired yet'); },
  searchRestaurants:       async (filters = {}) => {
    // Delegates to listRestaurants with simple client-side filter — keeps
    // the unified discover screen usable until the backend adds a real
    // search endpoint. Matches the shape the unified screen expects.
    const all = await listRestaurants();
    return { restaurants: all, total: all.length, total_pages: 1 };
  },
};

export default dataService;
